import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import apiWithAuth from '../../services/apiWithAuth';
import { 
  fetchShopCoupons, 
  createShopCoupon, 
  updateShopCoupon, 
  deleteShopCoupon 
} from '../../services/api';
import './ShopManagement.css';

/** Local start of day for YYYY-MM-DD from <input type="date" /> */
const dateInputStartTs = (yyyyMmDd) => {
  if (!yyyyMmDd || typeof yyyyMmDd !== 'string') return null;
  const parts = yyyyMmDd.split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  const [y, m, d] = parts;
  return new Date(y, m - 1, d, 0, 0, 0, 0).getTime();
};

/** Local end of day for YYYY-MM-DD */
const dateInputEndTs = (yyyyMmDd) => {
  if (!yyyyMmDd || typeof yyyyMmDd !== 'string') return null;
  const parts = yyyyMmDd.split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  const [y, m, d] = parts;
  return new Date(y, m - 1, d, 23, 59, 59, 999).getTime();
};

/** Offer overlaps [fromTs, toTs] when set; null bound means unbounded on that side */
const couponOverlapsFilterRange = (c, fromTs, toTs) => {
  const vf = new Date(c.validFrom).getTime();
  const vt = new Date(c.validTo).getTime();
  if (Number.isNaN(vf) || Number.isNaN(vt)) return false;
  if (fromTs !== null && vt < fromTs) return false;
  if (toTs !== null && vf > toTs) return false;
  return true;
};

const ShopOffers = () => {
  const location = useLocation();
  const [shops, setShops] = useState([]);
  const [selectedShop, setSelectedShop] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Coupon filters and form state
  const [filterType, setFilterType] = useState("");
  const [filterCustomerOnly, setFilterCustomerOnly] = useState(undefined);
  const [filterOnlineOnly, setFilterOnlineOnly] = useState(undefined);
  const [filterActive, setFilterActive] = useState(undefined);
  const [filterExpired, setFilterExpired] = useState(undefined);
  const [couponCodeSearch, setCouponCodeSearch] = useState('');
  const [filterOfferValidFromDate, setFilterOfferValidFromDate] = useState('');
  const [filterOfferValidToDate, setFilterOfferValidToDate] = useState('');
  const [couponSortConfig, setCouponSortConfig] = useState({ field: null, order: 'asc' });
  const [isCreating, setIsCreating] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [formData, setFormData] = useState({
    couponCode: "",
    type: "percentage",
    discount: "",
    maxDiscount: "",
    minOrderValue: "",
    maxOrderValue: "",
    buyQuantity: "",
    getFreeQuantity: "",
    usesPerCustomer: 1,
    validFrom: "",
    validTo: "",
    showToCustomerOnly: false,
    validForOnlinePaymentsOnly: false,
  });

  const getNowLocalDatetime = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);

  // Fetch all shops
  const fetchShops = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiWithAuth.get('/admin-panel/shops');
      if (response.data.success) {
        setShops(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to fetch shops');
      }
    } catch (err) {
      console.error('Error fetching shops:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch shops');
    } finally {
      setLoading(false);
    }
  };

  // Load coupons for selected shop
  const loadCoupons = useCallback(async () => {
    if (!selectedShop) return;
    
    try {
      setLoading(true);
      setError(null);
      const res = await fetchShopCoupons(selectedShop._id, {
        type: filterType || undefined,
        showToCustomerOnly: filterCustomerOnly,
        validForOnlinePaymentsOnly: filterOnlineOnly,
        active: filterActive,
        expired: filterExpired,
      });
      setCoupons(res?.data?.data || []);
    } catch (err) {
      setError("Failed to load coupons");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedShop, filterType, filterCustomerOnly, filterOnlineOnly, filterActive, filterExpired]);

  useEffect(() => {
    fetchShops();
  }, []);

  // Auto-select shop if coming from shop details page
  useEffect(() => {
    if (location.state?.selectedShopId && shops.length > 0) {
      const shop = shops.find(s => s._id === location.state.selectedShopId);
      if (shop) {
        setSelectedShop(shop);
      }
    }
  }, [location.state, shops]);

  useEffect(() => {
    if (selectedShop) {
      loadCoupons();
    }
  }, [selectedShop, loadCoupons]);

  // Filter shops based on search term and status
  const filteredShops = shops.filter(shop => {
    const q = searchTerm.toLowerCase();
    const matchesSearch =
      String(shop.name ?? '').toLowerCase().includes(q) ||
      String(shop.city ?? '').toLowerCase().includes(q);
    const matchesStatus = !statusFilter || shop.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const displayCoupons = useMemo(() => {
    let list = [...coupons];
    const q = couponCodeSearch.trim().toLowerCase();
    if (q) {
      list = list.filter((c) => String(c.couponCode || '').toLowerCase().includes(q));
    }
    const rangeFromTs = filterOfferValidFromDate ? dateInputStartTs(filterOfferValidFromDate) : null;
    const rangeToTs = filterOfferValidToDate ? dateInputEndTs(filterOfferValidToDate) : null;
    if (rangeFromTs !== null || rangeToTs !== null) {
      list = list.filter((c) => couponOverlapsFilterRange(c, rangeFromTs, rangeToTs));
    }
    if (couponSortConfig.field) {
      const mult = couponSortConfig.order === 'asc' ? 1 : -1;
      list.sort((a, b) => {
        if (couponSortConfig.field === 'type') {
          return mult * String(a.type || '').localeCompare(String(b.type || ''), undefined, { sensitivity: 'base' });
        }
        if (couponSortConfig.field === 'validFrom') {
          const ta = new Date(a.validFrom).getTime();
          const tb = new Date(b.validFrom).getTime();
          return mult * ((Number.isNaN(ta) ? 0 : ta) - (Number.isNaN(tb) ? 0 : tb));
        }
        if (couponSortConfig.field === 'validTo') {
          const ta = new Date(a.validTo).getTime();
          const tb = new Date(b.validTo).getTime();
          return mult * ((Number.isNaN(ta) ? 0 : ta) - (Number.isNaN(tb) ? 0 : tb));
        }
        return 0;
      });
    }
    return list;
  }, [coupons, couponCodeSearch, filterOfferValidFromDate, filterOfferValidToDate, couponSortConfig]);

  const hasOfferTableFilters =
    couponCodeSearch.trim().length > 0 ||
    Boolean(filterOfferValidFromDate) ||
    Boolean(filterOfferValidToDate);

  const handleCouponSort = (field) => {
    setCouponSortConfig((prev) => {
      const newOrder = prev.field === field && prev.order === 'asc' ? 'desc' : 'asc';
      return { field, order: newOrder };
    });
  };

  const getCouponSortIcon = (field) => {
    if (couponSortConfig.field !== field) return '↕';
    return couponSortConfig.order === 'asc' ? '↑' : '↓';
  };

  const handleShopSelect = (shop) => {
    setSelectedShop(shop);
    setCoupons([]);
    // Reset coupon filters when changing shops
    setFilterType("");
    setFilterCustomerOnly(undefined);
    setFilterOnlineOnly(undefined);
    setFilterActive(undefined);
    setFilterExpired(undefined);
    setCouponCodeSearch('');
    setFilterOfferValidFromDate('');
    setFilterOfferValidToDate('');
    setCouponSortConfig({ field: null, order: 'asc' });
    setIsCreating(false);
    resetForm();
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'APPROVED': return 'status-badge status-approved';
      case 'PENDING': return 'status-badge status-pending';
      case 'REJECTED': return 'status-badge status-rejected';
      case 'HOLD': return 'status-badge status-hold';
      default: return 'status-badge status-default';
    }
  };

  // Coupon management functions
  const normalizeNumber = (val) => {
    if (val === "" || val === null || val === undefined) return undefined;
    const n = Number(val);
    return Number.isNaN(n) ? undefined : n;
  };

  const resetForm = () => {
    setFormData({
      couponCode: "",
      type: "percentage",
      discount: "",
      maxDiscount: "",
      minOrderValue: "",
      maxOrderValue: "",
      buyQuantity: "",
      getFreeQuantity: "",
      usesPerCustomer: 1,
      validFrom: getNowLocalDatetime(),
      validTo: "",
      showToCustomerOnly: false,
      validForOnlinePaymentsOnly: false,
    });
    setEditingCode(null);
  };

  const buildCreatePayload = () => {
    const base = {
      shopId: selectedShop._id,
      userId: selectedShop.owner?._id || selectedShop.ownerId,
      couponCode: formData.couponCode.trim(),
      type: formData.type,
      validFrom: formData.validFrom || getNowLocalDatetime(),
      validTo: formData.validTo,
      showToCustomerOnly: !!formData.showToCustomerOnly,
      validForOnlinePaymentsOnly: !!formData.validForOnlinePaymentsOnly,
      usesPerCustomer: normalizeNumber(formData.usesPerCustomer) ?? 1,
      minOrderValue: normalizeNumber(formData.minOrderValue) ?? 0,
    };
    if (formData.type === "percentage") {
      base.discount = normalizeNumber(formData.discount);
      base.maxDiscount = normalizeNumber(formData.maxDiscount);
    } else if (formData.type === "flat") {
      base.discount = normalizeNumber(formData.discount);
    } else if (formData.type === "buyxgety") {
      base.buyQuantity = normalizeNumber(formData.buyQuantity);
      base.getFreeQuantity = normalizeNumber(formData.getFreeQuantity);
    }
    const maxOrder = normalizeNumber(formData.maxOrderValue);
    if (maxOrder !== undefined) base.maxOrderValue = maxOrder;
    return base;
  };

  const buildUpdatePayload = () => {
    const upd = {};
    upd.type = formData.type;
    if (formData.type === "percentage") {
      upd.discount = normalizeNumber(formData.discount);
      upd.maxDiscount = normalizeNumber(formData.maxDiscount);
    } else if (formData.type === "flat") {
      upd.discount = normalizeNumber(formData.discount);
    } else if (formData.type === "buyxgety") {
      upd.buyQuantity = normalizeNumber(formData.buyQuantity);
      upd.getFreeQuantity = normalizeNumber(formData.getFreeQuantity);
    }
    const mov = normalizeNumber(formData.minOrderValue);
    const xav = normalizeNumber(formData.maxOrderValue);
    if (mov !== undefined) upd.minOrderValue = mov;
    if (xav !== undefined) upd.maxOrderValue = xav;
    const upc = normalizeNumber(formData.usesPerCustomer);
    if (upc !== undefined) upd.usesPerCustomer = upc;
    if (formData.validFrom) upd.validFrom = formData.validFrom;
    if (formData.validTo) upd.validTo = formData.validTo;
    upd.showToCustomerOnly = !!formData.showToCustomerOnly;
    upd.validForOnlinePaymentsOnly = !!formData.validForOnlinePaymentsOnly;
    return upd;
  };

  const handleCreate = async () => {
    try {
      setLoading(true);
      setError(null);
      if (!formData.couponCode || !formData.type || !formData.validTo) {
        setError("Please fill all required fields.");
        setLoading(false);
        return;
      }
      const fromISO = (formData.validFrom || getNowLocalDatetime()).replace(" ", "T");
      const toISO = formData.validTo.replace(" ", "T");
      const fromDate = new Date(fromISO);
      const toDate = new Date(toISO);
      if (!(fromDate < toDate)) {
        setError("'Valid From' must be before 'Valid To'.");
        setLoading(false);
        return;
      }
      const payload = buildCreatePayload();
      await createShopCoupon(payload);
      resetForm();
      setIsCreating(false);
      await loadCoupons();
    } catch (err) {
      const message = err?.response?.data?.message || "Failed to create coupon";
      setError(message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingCode) return;
    try {
      setLoading(true);
      setError(null);
      const updateBody = buildUpdatePayload();
      await updateShopCoupon(selectedShop._id, editingCode, updateBody);
      resetForm();
      setIsCreating(false);
      await loadCoupons();
    } catch (err) {
      const message = err?.response?.data?.message || "Failed to update coupon";
      setError(message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;
    
    try {
      setLoading(true);
      setError(null);
      await deleteShopCoupon(selectedShop._id, code);
      await loadCoupons();
    } catch (err) {
      const message = err?.response?.data?.message || "Failed to delete coupon";
      setError(message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const populateFormForEdit = (c) => {
    setIsCreating(true);
    setEditingCode(c.couponCode);
    setFormData({
      couponCode: c.couponCode,
      type: c.type,
      discount: c.discount ?? "",
      maxDiscount: c.maxDiscount ?? "",
      minOrderValue: c.minOrderValue ?? "",
      maxOrderValue: c.maxOrderValue ?? "",
      buyQuantity: c.buyQuantity ?? "",
      getFreeQuantity: c.getFreeQuantity ?? "",
      usesPerCustomer: c.usesPerCustomer ?? 1,
      validFrom: c.validFrom
        ? new Date(c.validFrom).toISOString().slice(0, 16)
        : "",
      validTo: c.validTo ? new Date(c.validTo).toISOString().slice(0, 16) : "",
      showToCustomerOnly: !!c.showToCustomerOnly,
      validForOnlinePaymentsOnly: !!c.validForOnlinePaymentsOnly,
    });
  };

  const onChangeField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const canSubmit = () => {
    if (!formData.couponCode && !editingCode) return false;
    if (!formData.type) return false;
    if (!formData.validFrom || !formData.validTo) return false;
    if (formData.type === "percentage") {
      return formData.discount !== "" && formData.maxDiscount !== "";
    }
    if (formData.type === "flat") {
      return formData.discount !== "";
    }
    if (formData.type === "buyxgety") {
      return formData.buyQuantity !== "" && formData.getFreeQuantity !== "";
    }
    return true;
  };

  return (
    <div className="shop-management-wrapper" style={{ width: '100%', height: '100vh', isolation: 'isolate' }}>
      <div className="shop-management">
      {/* Header */}
      <div className="shop-management-header">
        <div>
          <h1>Shop Offers Management</h1>
          <p>Create and manage promotional offers for all shops</p>
        </div>
        <div className="header-actions">
          <span className="text-sm text-gray-600">
            {selectedShop
              ? hasOfferTableFilters
                ? `${displayCoupons.length} of ${coupons.length} coupons`
                : `${coupons.length} coupons`
              : `${shops.length} shops`}
          </span>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">!</span>
          {error}
        </div>
      )}

      {/* Compact Filter Toolbar */}
      <div className="filters-toolbar">
        <div className="filter-group">
          <label className="filter-label">Search</label>
          <input
            type="text"
            placeholder="Search shops..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="filter-input search-input"
          />
        </div>

        <div className="toolbar-divider"></div>

        <div className="filter-group">
          <label className="filter-label">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Status</option>
            <option value="APPROVED">Approved</option>
            <option value="PENDING">Pending</option>
            <option value="REJECTED">Rejected</option>
            <option value="HOLD">Hold</option>
          </select>
        </div>

        {selectedShop && (
          <>
            <div className="toolbar-divider"></div>

            <div className="filter-group">
              <label className="filter-label">Type</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="filter-select"
              >
                <option value="">All Types</option>
                <option value="percentage">Percentage</option>
                <option value="flat">Flat Discount</option>
                <option value="buyxgety">Buy X Get Y</option>
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">Offer code</label>
              <input
                type="text"
                placeholder="Search by code..."
                value={couponCodeSearch}
                onChange={(e) => setCouponCodeSearch(e.target.value)}
                className="filter-input search-input"
              />
            </div>

            <div className="filter-group">
              <label className="filter-label">Valid from</label>
              <input
                type="date"
                value={filterOfferValidFromDate}
                onChange={(e) => setFilterOfferValidFromDate(e.target.value)}
                className="filter-input"
                title="Include offers that are still active on or after this date (offer end ≥ this day)"
              />
            </div>

            <div className="filter-group">
              <label className="filter-label">Valid to</label>
              <input
                type="date"
                value={filterOfferValidToDate}
                onChange={(e) => setFilterOfferValidToDate(e.target.value)}
                className="filter-input"
                title="Include offers that have started on or before this date (offer start ≤ this day)"
              />
            </div>

            <label className="filter-checkbox">
              <input
                type="checkbox"
                checked={!!filterCustomerOnly}
                onChange={(e) => setFilterCustomerOnly(e.target.checked ? true : undefined)}
              />
              Customer Only
            </label>

            <label className="filter-checkbox">
              <input
                type="checkbox"
                checked={!!filterOnlineOnly}
                onChange={(e) => setFilterOnlineOnly(e.target.checked ? true : undefined)}
              />
              Online Only
            </label>

            <label className="filter-checkbox">
              <input
                type="checkbox"
                checked={!!filterActive}
                onChange={(e) => setFilterActive(e.target.checked ? true : undefined)}
              />
              Active
            </label>

            <label className="filter-checkbox">
              <input
                type="checkbox"
                checked={!!filterExpired}
                onChange={(e) => setFilterExpired(e.target.checked ? true : undefined)}
              />
              Expired
            </label>

            <div className="toolbar-divider"></div>

            <button
              onClick={() => {
                if (!isCreating) {
                  resetForm();
                  setIsCreating(true);
                } else {
                  setIsCreating(false);
                  resetForm();
                }
              }}
              className="btn btn-primary"
            >
              {isCreating ? "Cancel" : "New Coupon"}
            </button>
          </>
        )}
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Sidebar - Shop List */}
        <div className="sidebar">
          <div className="sidebar-header">
            <h3 className="sidebar-title">Shops</h3>
            <p className="sidebar-subtitle">Select shop to manage offers</p>
          </div>

          <div className="sidebar-list">
            {loading && !selectedShop ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading shops...</p>
              </div>
            ) : (
              filteredShops.map(shop => (
                <div
                  key={shop._id}
                  className={`sidebar-item ${selectedShop?._id === shop._id ? 'selected' : ''}`}
                  onClick={() => handleShopSelect(shop)}
                >
                  <div className="sidebar-item-header">
                    <img 
                      src={shop.logoUrl || '/KD-icon.png'} 
                      alt={shop.name}
                      className="sidebar-item-logo"
                      onError={(e) => { e.target.src = '/KD-icon.png'; }}
                    />
                    <h4 className="sidebar-item-name">{shop.name}</h4>
                  </div>
                  <p className="sidebar-item-location">{shop.city}, {shop.state}</p>
                  <span className={`sidebar-item-status ${getStatusBadgeClass(shop.status).replace('status-badge ', '')}`}>
                    {shop.status}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Data Area */}
        <div className="data-area">
          {!selectedShop ? (
            <div className="empty-state">
              <div className="empty-icon">🎫</div>
              <div className="empty-title">Select a shop to manage offers</div>
              <div className="empty-description">
                Choose a shop from the sidebar to create and manage promotional coupons
              </div>
            </div>
          ) : (
            <>
              <div className="data-header">
                <div>
                  <h2 className="data-title">Offers for {selectedShop.name}</h2>
                  <p className="data-subtitle">
                    {selectedShop.city}, {selectedShop.state} •{' '}
                    {hasOfferTableFilters
                      ? `${displayCoupons.length} of ${coupons.length} coupons`
                      : `${coupons.length} coupons`}
                  </p>
                </div>
              </div>

              {/* Coupon Creation/Edit Form */}
              {isCreating && (
                <div className="form-modal" onClick={() => { resetForm(); setIsCreating(false); }}>
                  <div className="form-container" onClick={(e) => e.stopPropagation()}>
                    <div className="form-header">
                      <h2 className="form-title">{editingCode ? 'Edit Coupon' : 'Create New Coupon'}</h2>
                      <button className="form-close" onClick={() => { resetForm(); setIsCreating(false); }}>×</button>
                    </div>
                    
                    <div className="form-content">
                      <div className="form-section">
                        <h3 className="form-section-title">Basic Information</h3>
                        <div className="form-grid">
                          <div className="form-group">
                            <label className="form-label">Coupon Code *</label>
                            <input
                              type="text"
                              value={formData.couponCode}
                              onChange={(e) => onChangeField("couponCode", e.target.value.toUpperCase())}
                              disabled={!!editingCode}
                              className="form-input"
                              placeholder="Enter coupon code"
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Discount Type *</label>
                            <select
                              value={formData.type}
                              onChange={(e) => onChangeField("type", e.target.value)}
                              className="form-select"
                            >
                              <option value="percentage">Percentage Discount</option>
                              <option value="flat">Flat Amount Discount</option>
                              <option value="buyxgety">Buy X Get Y Free</option>
                            </select>
                          </div>

                          {(formData.type === "percentage" || formData.type === "flat") && (
                            <div className="form-group">
                              <label className="form-label">Discount Value * {formData.type === "percentage" ? "(%)" : "(₹)"}</label>
                              <input
                                type="number"
                                min="0"
                                value={formData.discount}
                                onChange={(e) => onChangeField("discount", e.target.value)}
                                className="form-input"
                                placeholder={formData.type === "percentage" ? "e.g., 10" : "e.g., 100"}
                              />
                            </div>
                          )}

                          {formData.type === "percentage" && (
                            <div className="form-group">
                              <label className="form-label">Max Discount Amount * (₹)</label>
                              <input
                                type="number"
                                min="0"
                                value={formData.maxDiscount}
                                onChange={(e) => onChangeField("maxDiscount", e.target.value)}
                                className="form-input"
                                placeholder="e.g., 500"
                              />
                            </div>
                          )}

                          {formData.type === "buyxgety" && (
                            <>
                              <div className="form-group">
                                <label className="form-label">Buy Quantity *</label>
                                <input
                                  type="number"
                                  min="1"
                                  value={formData.buyQuantity}
                                  onChange={(e) => onChangeField("buyQuantity", e.target.value)}
                                  className="form-input"
                                  placeholder="e.g., 2"
                                />
                              </div>
                              <div className="form-group">
                                <label className="form-label">Get Free Quantity *</label>
                                <input
                                  type="number"
                                  min="1"
                                  value={formData.getFreeQuantity}
                                  onChange={(e) => onChangeField("getFreeQuantity", e.target.value)}
                                  className="form-input"
                                  placeholder="e.g., 1"
                                />
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="form-section">
                        <h3 className="form-section-title">Order Constraints</h3>
                        <div className="form-grid">
                          <div className="form-group">
                            <label className="form-label">Min Order Value (₹)</label>
                            <input
                              type="number"
                              min="0"
                              value={formData.minOrderValue}
                              onChange={(e) => onChangeField("minOrderValue", e.target.value)}
                              className="form-input"
                              placeholder="e.g., 500"
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Max Order Value (₹)</label>
                            <input
                              type="number"
                              min="0"
                              value={formData.maxOrderValue}
                              onChange={(e) => onChangeField("maxOrderValue", e.target.value)}
                              className="form-input"
                              placeholder="e.g., 5000"
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Uses Per Customer</label>
                            <input
                              type="number"
                              min={1}
                              value={formData.usesPerCustomer}
                              onChange={(e) => onChangeField("usesPerCustomer", parseInt(e.target.value || "1", 10))}
                              className="form-input"
                              placeholder="1"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="form-section">
                        <h3 className="form-section-title">Validity Period</h3>
                        <div className="form-grid">
                          <div className="form-group">
                            <label className="form-label">Valid From *</label>
                            <input
                              type="datetime-local"
                              value={formData.validFrom}
                              onChange={(e) => onChangeField("validFrom", e.target.value)}
                              className="form-input"
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Valid To *</label>
                            <input
                              type="datetime-local"
                              value={formData.validTo}
                              onChange={(e) => onChangeField("validTo", e.target.value)}
                              className="form-input"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="form-section">
                        <h3 className="form-section-title">Options</h3>
                        <div className="form-grid">
                          <div className="form-group">
                            <label className="filter-checkbox">
                              <input
                                type="checkbox"
                                checked={formData.showToCustomerOnly}
                                onChange={(e) => onChangeField("showToCustomerOnly", e.target.checked)}
                              />
                              Show To Customer Only
                            </label>
                          </div>
                          <div className="form-group">
                            <label className="filter-checkbox">
                              <input
                                type="checkbox"
                                checked={formData.validForOnlinePaymentsOnly}
                                onChange={(e) => onChangeField("validForOnlinePaymentsOnly", e.target.checked)}
                              />
                              Valid For Online Payments Only
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="form-footer">
                      <button className="btn btn-secondary" onClick={() => { resetForm(); setIsCreating(false); }}>
                        Cancel
                      </button>
                      {editingCode ? (
                        <button
                          disabled={!canSubmit() || loading}
                          onClick={handleUpdate}
                          className="btn btn-primary"
                        >
                          {loading ? 'Updating...' : 'Update Coupon'}
                        </button>
                      ) : (
                        <button
                          disabled={!canSubmit() || loading}
                          onClick={handleCreate}
                          className="btn btn-success"
                        >
                          {loading ? 'Creating...' : 'Create Coupon'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Coupons Table */}
              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th className="sortable" onClick={() => handleCouponSort('type')}>
                        Type {getCouponSortIcon('type')}
                      </th>
                      <th>Discount</th>
                      <th>Max Discount</th>
                      <th>Min Order</th>
                      <th>Max Order</th>
                      <th>Uses</th>
                      <th className="sortable" onClick={() => handleCouponSort('validFrom')}>
                        Valid From {getCouponSortIcon('validFrom')}
                      </th>
                      <th className="sortable" onClick={() => handleCouponSort('validTo')}>
                        Valid To {getCouponSortIcon('validTo')}
                      </th>
                      <th>Flags</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="11">
                          <div className="loading-state">
                            <div className="loading-spinner"></div>
                            <p>Loading coupons...</p>
                          </div>
                        </td>
                      </tr>
                    ) : coupons.length === 0 ? (
                      <tr>
                        <td colSpan="11">
                          <div className="empty-state">
                            <div className="empty-icon">🎫</div>
                            <div className="empty-title">No coupons found</div>
                            <div className="empty-description">
                              This shop doesn't have any coupons yet. Create one to get started!
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : displayCoupons.length === 0 ? (
                      <tr>
                        <td colSpan="11">
                          <div className="empty-state">
                            <div className="empty-icon">🔍</div>
                            <div className="empty-title">No matching offers</div>
                            <div className="empty-description">
                              Try a different code search, adjust the valid from / valid to dates, or clear filters.
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      displayCoupons.map((c) => (
                        <tr key={c._id}>
                          <td className="font-semibold">{c.couponCode}</td>
                          <td>
                            <span className="status-badge status-default">{c.type}</span>
                          </td>
                          <td>
                            {c.type === "buyxgety"
                              ? `Buy ${c.buyQuantity} Get ${c.getFreeQuantity}`
                              : `${c.discount}${c.type === "percentage" ? "%" : "₹"}`}
                          </td>
                          <td>{c.type === "percentage" ? `₹${c.maxDiscount ?? "-"}` : '-'}</td>
                          <td>₹{c.minOrderValue ?? 0}</td>
                          <td>₹{c.maxOrderValue ?? '-'}</td>
                          <td>{c.usesPerCustomer}</td>
                          <td>{new Date(c.validFrom).toLocaleDateString()}</td>
                          <td>{new Date(c.validTo).toLocaleDateString()}</td>
                          <td>
                            <div className="flex gap-2">
                              {c.showToCustomerOnly && <span className="status-badge status-pending">Customer</span>}
                              {c.validForOnlinePaymentsOnly && <span className="status-badge status-pending">Online</span>}
                            </div>
                          </td>
                          <td>
                            <div className="table-actions">
                              <button
                                className="btn btn-sm btn-warning"
                                onClick={() => populateFormForEdit(c)}
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleDelete(c.couponCode)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default ShopOffers;